import { apiBinary, ApiError } from '../api.js'
import { responseFileName } from '../reports/exportDownload.js'
import { filterPayload as transactionFilters } from './transactions.js'
import { filterPayload as measurementFilters } from './tankMeasurements.js'
import { filterPayload as deliveryFilters } from './deliveries.js'

const journals = new Set(['transactions', 'tank-measurements', 'in-tank-deliveries'])
const formats = new Set(['csv', 'xlsx', 'html'])
const filterPayloads = { transactions: transactionFilters, 'tank-measurements': measurementFilters, 'in-tank-deliveries': deliveryFilters }

export function exportFilename(disposition, format) {
  return responseFileName(disposition, `.${format}`, 'pts2-export')
}

export function exportFilterSnapshot(filters) {
  const { page, limit, ...snapshot } = JSON.parse(JSON.stringify(filters))
  return Object.fromEntries(Object.entries(snapshot).filter(([, value]) => value != null))
}

// One request owns one immutable applied-filter snapshot. Invalidated responses never deliver files.
export function createHistoryExport({ request = apiBinary, onState = () => {}, deliver }) {
  let generation = 0, active = null, disposed = false
  function cancel() {
    generation++; active?.controller.abort(); active?.popup?.close(); active = null
    onState({ busy: false, error: null, format: null })
  }
  async function start(journal, format, filters, popup = null) {
    if (active || disposed) { popup?.close(); return null }
    if (!journals.has(journal) || !formats.has(format)) throw new Error('Unsupported export')
    const token = ++generation, controller = new AbortController()
    const operation = { controller, popup }; active = operation
    onState({ busy: true, error: null, format })
    try {
      const result = await request(`/pts2/${journal}/export`, { method: 'POST', signal: controller.signal,
        body: JSON.stringify({ format, filters: filterPayloads[journal](exportFilterSnapshot(filters)) }) })
      if (disposed || token !== generation || controller.signal.aborted) return null
      if (!result.blob?.size) throw new ApiError('Empty export', 422, 'pts2_export_empty')
      const media = { csv: 'text/csv', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', html: 'text/html' }[format]
      if (result.blob.type.toLowerCase().split(';')[0].trim() !== media) throw new ApiError('Unexpected export content type', 502, 'invalid_export_content_type')
      await deliver({ ...result, filename: exportFilename(result.disposition, format), format, popup })
      return true
    } catch (error) {
      popup?.close()
      if (token === generation && !disposed && error?.name !== 'AbortError') onState({ busy: false, error, format })
      return null
    } finally {
      if (token === generation) { active = null; onState({ busy: false }) }
    }
  }
  return { start, cancel, dispose() { disposed = true; cancel() } }
}
